import {
  createRealtimeCall,
  type RealtimeCall,
  type RealtimeSignaling,
  realtimeFragment,
} from '@crowdex/provider-contracts/realtime-call';
import { useLayoutEffect, useRef, useState } from 'react';
import type { DictationSettings } from './settings';
import { defaultSpeechLanguage } from './speech/language';

/**
 * Where audio goes: the user's realtime provider signs the call (`realtime.v1`); the page runs the
 * call itself.
 */
export type VoiceTransports = {
  realtimeSignaling: RealtimeSignaling;
};

const LANGUAGE_NAMES = new Intl.DisplayNames(['en'], { type: 'language' });
/** Once stopped, the words are all in when none came for this long. */
const QUIET_MS = 1_200;

type Recording = {
  controller: AbortController;
  onText: (text: string) => void;
  stream?: MediaStream;
  realtimeCall?: RealtimeCall;
  timer?: ReturnType<typeof setTimeout>;
  /** Waits for the last words once stopped. */
  quiet?: ReturnType<typeof setTimeout>;
  stopping?: boolean;
  /** The user's words so far, as the call transcribes them. */
  text: string;
};
function release(session: Recording) {
  clearTimeout(session.timer);
  clearTimeout(session.quiet);
  session.controller.abort();
  void session.realtimeCall?.close();
  session.stream?.getTracks().forEach((track) => {
    track.stop();
  });
}

export function useVoiceInput(
  context: string,
  onText: (text: string) => void,
  readSettings: () => DictationSettings,
  transports: VoiceTransports,
) {
  const [status, setStatus] = useState<'idle' | 'starting' | 'recording' | 'transcribing'>('idle');
  const [error, setError] = useState<string | null>(null);
  const active = useRef<Recording | null>(null);

  function cancel() {
    const session = active.current;
    active.current = null;
    if (session) release(session);
    setStatus('idle');
  }
  useLayoutEffect(() => {
    void context;
    setStatus('idle');
    setError(null);
    return () => {
      const session = active.current;
      active.current = null;
      if (session) release(session);
    };
  }, [context]);

  function fail(session: Recording, cause: unknown) {
    if (active.current !== session) return;
    cancel();
    setError(
      cause instanceof DOMException && cause.name === 'NotAllowedError'
        ? 'Microphone access was denied. Allow microphone access in your browser or system settings.'
        : cause instanceof Error
          ? cause.message
          : 'Could not start dictation.',
    );
  }

  /** Inserts what the user said, and ends the call. */
  function finish(session: Recording) {
    if (active.current !== session) return;
    const text = session.text.replace(/\s+/g, ' ').trim();
    if (!text) return;
    session.onText(text);
    cancel();
  }

  /** Once stopped, finishes when no words came for a moment. */
  function waitQuiet(session: Recording) {
    clearTimeout(session.quiet);
    session.quiet = setTimeout(() => finish(session), QUIET_MS);
  }

  function stop() {
    const session = active.current;
    if (!session || session.stopping || !session.realtimeCall) return;
    session.stopping = true;
    clearTimeout(session.timer);
    setStatus('transcribing');
    session.stream?.getAudioTracks().forEach((track) => {
      track.enabled = false;
    });
    waitQuiet(session);
    session.timer = setTimeout(
      () => fail(session, new Error('The voice call did not return a transcription.')),
      12_000,
    );
  }

  async function start() {
    if (active.current) return;
    setError(null);
    const settings = readSettings();
    if (!navigator.mediaDevices?.getUserMedia) {
      setError(
        'Microphone recording is unavailable. Use the desktop app or a supported HTTPS browser.',
      );
      return;
    }
    const session: Recording = { controller: new AbortController(), onText, text: '' };
    active.current = session;
    setStatus('starting');
    try {
      const language =
        settings.speechLanguage === 'system'
          ? defaultSpeechLanguage(navigator.languages)
          : settings.speechLanguage;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      session.stream = stream;
      if (active.current !== session) return release(session);
      session.realtimeCall = await createRealtimeCall({
        signaling: transports.realtimeSignaling,
        language,
        stream,
        signal: session.controller.signal,
        instructions:
          language === 'fr'
            ? 'Transcris fidèlement la voix française. Ne réponds pas à son contenu.'
            : `The user speaks ${LANGUAGE_NAMES.of(language) ?? language}. Transcribe them faithfully in that language. Do not answer the content.`,
        onEvent: (event) => {
          const said = realtimeFragment(event);
          if (said === null || active.current !== session) return;
          // The call answering means the user is done; it is not dictated.
          if (said.role === 'assistant') return finish(session);
          session.text += said.text;
          if (session.stopping) waitQuiet(session);
        },
      });
      if (active.current !== session) return release(session);
      session.timer = setTimeout(stop, 120_000);
      setStatus('recording');
    } catch (cause) {
      fail(session, cause);
    }
  }
  return { status, error, start, cancel, stop };
}
